// Small form fields shared by the Ниши and Градове editors. Text arrays are edited as one entry
// per line: the owner pastes a list, not JSON.

import { useState } from 'react';

export function linesToArray(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

export function arrayToLines(items: readonly string[]): string {
  return items.join('\n');
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  placeholder,
  mono,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  hint?: string;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="adm__field">
      <span className="adm__field-label">{label}</span>
      <input
        type="text"
        className={`adm__input${mono ? ' adm__input--mono' : ''}`}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint ? <span className="adm__field-hint">{hint}</span> : null}
    </label>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  hint,
  min,
  max,
}: {
  label: string;
  value: number | null;
  onChange: (next: number | null) => void;
  hint?: string;
  min?: number;
  max?: number;
}) {
  return (
    <label className="adm__field">
      <span className="adm__field-label">{label}</span>
      <input
        type="number"
        className="adm__input adm__input--mono"
        value={value ?? ''}
        min={min}
        max={max}
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
      />
      {hint ? <span className="adm__field-hint">{hint}</span> : null}
    </label>
  );
}

/** A text[] column as a textarea, one entry per line. The parent remounts it when the row changes. */
export function LinesField({
  label,
  value,
  onChange,
  hint,
  rows = 4,
}: {
  label: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
  hint?: string;
  rows?: number;
}) {
  const [text, setText] = useState(() => arrayToLines(value));
  return (
    <label className="adm__field">
      <span className="adm__field-label">
        {label} <span className="adm__field-count">{value.length}</span>
      </span>
      <textarea
        className="adm__textarea"
        rows={rows}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          onChange(linesToArray(event.target.value));
        }}
      />
      {hint ? <span className="adm__field-hint">{hint}</span> : null}
    </label>
  );
}

export function ToggleField({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="adm__field adm__field--row">
      <input
        type="checkbox"
        className="adm__toggle-check"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="adm__field-label">{label}</span>
      {hint ? <span className="adm__field-hint">{hint}</span> : null}
    </label>
  );
}
